/**
 * Billing API Functions
 * 
 * Centralized API calls for billing, subscriptions, and checkout.
 * 
 * IMPROVEMENTS over Svelte version:
 * - Consolidated into single file
 * - Strongly typed responses
 * - Better error handling with specific error types
 */

import type {
  CancelFlowMode,
  CancelOutcome,
  CancelSurface,
  CancellationReason,
  RecoveryOffer,
} from '@/lib/constants/cancellationReasons'
import type {
  AutoTopUpStatus,
  BillingStatus,
  CreateCheckoutSessionResponse,
  PortalLinkResponse,
} from '@/lib/types/billing'
import { getAcquisitionContext } from '@/lib/utils/acquisitionContext'
import type { ObservabilityScope } from '@/lib/observability/shared'

const PUBLIC_IS_PROD = import.meta.env.VITE_PUBLIC_IS_PROD
const PUBLIC_IS_LOCAL = import.meta.env.VITE_PUBLIC_IS_LOCAL

const API_URL =
  PUBLIC_IS_PROD === 'TRUE'
    ? 'https://app.humblytics.com/api/v1/billing'
    : PUBLIC_IS_LOCAL === 'TRUE'
    ? 'http://localhost:3000/api/v1/billing'
    : 'https://humblytics.onrender.com/api/v1/billing'

// ============================================================================
// Error Types
// ============================================================================

class BillingError extends Error {
  code: string
  status?: number

  constructor(
    message: string,
    code: string,
    status?: number
  ) {
    super(message)
    this.name = 'BillingError'
    this.code = code
    this.status = status
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

function buildAuthHeaders(token: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

/**
 * Get Humblytics view ID for revenue attribution
 */
function getHumblyticsViewId(): string | undefined {
  if (typeof window === 'undefined') return undefined
  
  // Check cookie first
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [key, ...valueParts] = cookie.split('=')
    if (key?.trim() === 'session_rev_ref') {
      const value = valueParts.join('=').trim()
      if (value) {
        try {
          return decodeURIComponent(value)
        } catch {
          return value
        }
      }
    }
  }
  
  // Fallback to window object
  const humblytics = (window as any).Humblytics
  if (humblytics?.viewId) {
    return String(humblytics.viewId)
  }
  
  return undefined
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get the current billing status for the user
 */
export async function getBillingStatus(token: string): Promise<BillingStatus> {
  const response = await fetch(`${API_URL}/status`, {
    method: 'GET',
    headers: buildAuthHeaders(token),
    credentials: 'include',
  })

  if (!response.ok) {
    throw new BillingError(
      'Failed to fetch billing status',
      'billing_status_failed',
      response.status
    )
  }

  return response.json()
}


/**
 * Records what the account is for (`PUT /api/v1/account/preferences`). "agent"
 * also starts an account with no plan on the free 25,000 data points.
 */
export async function saveWorkMode(
  token: string,
  workMode: 'agent' | 'dashboard' | 'both'
): Promise<{ workMode: string; freeStartGranted: boolean; planKind: string }> {
  const response = await fetch(API_URL.replace('/billing', '/account/preferences'), {
    method: 'PUT',
    headers: buildAuthHeaders(token),
    credentials: 'include',
    body: JSON.stringify({ workMode }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new BillingError(
      data.message || 'Failed to save the work mode',
      data.error || 'account_preferences_failed',
      response.status
    )
  }
  return data
}

/**
 * Saves the Agents plan auto top-up setting. Saving again after a declined
 * charge clears the block and retries at once if usage is past the threshold.
 * Errors carry the server code: 400 `no_payment_method` / `not_api_plan` /
 * `invalid_cap`, 503 `topup_unavailable`.
 */
export async function updateAutoTopUp(
  token: string,
  settings: { enabled: boolean; maxPerPeriod?: number }
): Promise<AutoTopUpStatus> {
  const response = await fetch(`${API_URL}/auto-top-up`, {
    method: 'PUT',
    headers: buildAuthHeaders(token),
    credentials: 'include',
    body: JSON.stringify(settings),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new BillingError(
      data.message || 'Failed to update auto top-up',
      data.error || 'update_auto_top_up_failed',
      response.status
    )
  }
  return data
}

/**
 * Buys prepaid data points on the Agents plan (`POST /api/v1/billing/top-up`),
 * charged to the card saved at the $39 checkout. Tracking resumes right away.
 * Errors carry the server code: 400 `not_api_plan` / `no_payment_method`,
 * 402 `requires_action` (the bank wants to confirm) / `payment_failed`,
 * 503 `topup_unavailable`.
 */
export async function buyTopUp(
  token: string,
  blocks = 1
): Promise<{ points: number; limit: number; trackingPaused: boolean }> {
  const response = await fetch(`${API_URL}/top-up`, {
    method: 'POST',
    headers: { ...buildAuthHeaders(token), 'Idempotency-Key': crypto.randomUUID() },
    credentials: 'include',
    body: JSON.stringify({ blocks }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new BillingError(
      data.message || 'Failed to buy data points',
      data.error || 'top_up_failed',
      response.status
    )
  }
  return data
}

/**
 * Agents plan checkout: a one-time $39 Stripe payment for 100,000 data
 * points (not a subscription). Returns the Stripe Checkout URL to redirect
 * to. Errors carry the server code: 409 `already_subscribed`, 503
 * `checkout_unavailable` while the price env is unset.
 */
export async function createAgentsCheckout(
  token: string,
  options: { successPath?: string; cancelPath?: string } = {}
): Promise<{ url: string; sessionId: string }> {
  const response = await fetch(`${API_URL}/create-checkout-session`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
    credentials: 'include',
    body: JSON.stringify({
      priceId: 'agents',
      humblyticsViewId: getHumblyticsViewId(),
      successUrl: options.successPath,
      cancelUrl: options.cancelPath,
    }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok || !data.url) {
    throw new BillingError(
      data.message || 'Failed to start the Agents plan checkout',
      data.error || 'create_checkout_session_failed',
      response.status
    )
  }
  return data
}

/**
 * Applies a paid Agents checkout right away on the success page, so the plan
 * shows without waiting for the Stripe webhook. Safe to call more than once.
 */
export async function finalizeAgentsCheckout(
  token: string,
  sessionId: string
): Promise<{ paid: boolean }> {
  const response = await fetch(`${API_URL}/finalize-checkout`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
    credentials: 'include',
    body: JSON.stringify({ sessionId }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new BillingError(
      data.message || 'Failed to confirm the Agents plan checkout',
      data.error || 'finalize_checkout_failed',
      response.status
    )
  }
  return data
}

/**
 * Create a Stripe checkout session
 */
async function createCheckoutSession(
  token: string,
  priceId: string,
  promotionCode?: string
): Promise<CreateCheckoutSessionResponse> {
  const response = await fetch(`${API_URL}/create-checkout-session`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
    credentials: 'include',
    body: JSON.stringify({
      priceId,
      promotionCode,
      humblyticsViewId: getHumblyticsViewId(),
    }),
  })

  if (!response.ok) {
    throw new BillingError(
      'Failed to create checkout session',
      'create_checkout_session_failed',
      response.status
    )
  }

  return response.json()
}

/**
 * Create a Stripe subscription (for inline checkout)
 */
async function createSubscription(
  token: string,
  priceId: string,
  options?: {
    promotionCode?: string
    paymentMethodId?: string
    chargeImmediately?: boolean
  }
): Promise<any> {
  const response = await fetch(`${API_URL}/create-subscription`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
    credentials: 'include',
    body: JSON.stringify({
      priceId,
      promotionCode: options?.promotionCode,
      paymentMethodId: options?.paymentMethodId,
      chargeImmediately: options?.chargeImmediately,
      humblyticsViewId: getHumblyticsViewId(),
    }),
  })

  if (!response.ok) {
    throw new BillingError(
      'Failed to create subscription',
      'create_subscription_failed',
      response.status
    )
  }

  return response.json()
}

/**
 * Create a setup intent for payment method collection
 */
async function createSetupIntent(token: string): Promise<{ clientSecret: string }> {
  const response = await fetch(`${API_URL}/create-setup-intent`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
    credentials: 'include',
    body: JSON.stringify({
      humblyticsViewId: getHumblyticsViewId(),
    }),
  })

  if (!response.ok) {
    throw new BillingError(
      'Failed to create setup intent',
      'create_setup_intent_failed',
      response.status
    )
  }

  return response.json()
}

/**
 * Finalize a subscription after payment confirmation
 */
async function finalizeSubscription(
  token: string,
  subscriptionId: string
): Promise<{ success: boolean }> {
  const response = await fetch(`${API_URL}/finalize-subscription`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
    credentials: 'include',
    body: JSON.stringify({ subscriptionId }),
  })

  if (!response.ok) {
    throw new BillingError(
      'Failed to finalize subscription',
      'finalize_subscription_failed',
      response.status
    )
  }

  return response.json()
}

export type PortalLinkFlow = 'cancel' | 'update'

export type PortalLinkOptions = {
  /**
   * Deep-link the session into a Stripe Customer Portal flow.
   * `cancel` → `subscription_cancel`. `update` → `subscription_update`
   * (or `subscription_update_confirm` when `priceId` is also sent).
   */
  flow?: PortalLinkFlow
  /** Stripe `sub_…` id — required by cancel and update deep links. */
  subscriptionId?: string | null
  /**
   * Stripe `price_…` id for the plan the user picked in-app. Backend may
   * promote `flow=update` to `subscription_update_confirm` when it can
   * resolve the subscription item; otherwise ignore.
   */
  priceId?: string | null
  /** Where the fallback log is attributed. Hub/settings may only have `planTier`. */
  scope?: ObservabilityScope
}

export function buildPortalLinkPath(options?: PortalLinkOptions): string {
  const params = new URLSearchParams()
  if (options?.flow) params.set('flow', options.flow)
  if (options?.subscriptionId) params.set('subscription', options.subscriptionId)
  if (options?.priceId) params.set('price', options.priceId)
  const query = params.toString()
  return `get-billing-portal-link${query ? `?${query}` : ''}`
}

const PORTAL_FLOWS = new Set(['cancel', 'update', 'generic'])

/**
 * Get the Stripe billing portal link.
 * The backend builds the `flow_data` session. Until it does, the response
 * omits `flow` and the generic portal URL is used as-is.
 */
export async function getPortalLink(
  token: string,
  options?: PortalLinkOptions
): Promise<{ url: string; flow: 'cancel' | 'update' | 'generic' }> {
  // Note: This endpoint is at the root API, not under /billing
  const rootApiUrl = API_URL.replace('/billing', '')
  const url = `${rootApiUrl}/${buildPortalLinkPath(options)}`

  const response = await fetch(url, {
    method: 'GET',
    headers: buildAuthHeaders(token),
  })

  if (!response.ok) {
    throw new BillingError(
      'Failed to get billing portal link',
      'get_portal_link_failed',
      response.status
    )
  }

  const data = (await response.json()) as PortalLinkResponse
  const flow = PORTAL_FLOWS.has(data.flow ?? '')
    ? (data.flow as 'cancel' | 'update' | 'generic')
    : 'generic'

  if (options?.flow && flow === 'generic') {
    const { logPortalFlowFallback } = await import('@/lib/observability/billing')
    logPortalFlowFallback(options.scope ?? {}, { requestedFlow: options.flow })
  }

  return { url: data.url, flow }
}

export type CancelIntentPayload = {
  reason: CancellationReason
  reason_detail?: string | null
  offer_shown: RecoveryOffer
  offer_accepted: boolean
  surface: CancelSurface
  mode: CancelFlowMode
  outcome: CancelOutcome
}

export type CancelIntentResult = {
  /** True only when a too_expensive accept actually put the coupon on the subscription. */
  discountApplied: boolean
}

/**
 * Record a cancellation-flow outcome. A `retained_discount` accept also
 * applies the retention coupon server-side; no email is sent.
 * Throws `BillingError` (code = server `error` string) on non-2xx.
 */
export async function postCancelIntent(
  token: string,
  payload: CancelIntentPayload
): Promise<CancelIntentResult> {
  const response = await fetch(`${API_URL}/cancel-intent`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
    credentials: 'include',
    body: JSON.stringify(payload),
    keepalive: true,
  })
  const data = (await response.json().catch(() => ({}))) as {
    error?: string
    discount_applied?: boolean
  }
  if (!response.ok) {
    throw new BillingError(
      'Failed to record cancel intent',
      data.error ?? 'cancel_intent_failed',
      response.status
    )
  }
  return { discountApplied: data.discount_applied === true }
}

/**
 * Cancel the current subscription
 */
async function cancelSubscription(
  token: string,
  reason?: string
): Promise<{ success: boolean }> {
  const response = await fetch(`${API_URL}/cancel`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
    credentials: 'include',
    body: JSON.stringify({ reason }),
  })

  if (!response.ok) {
    throw new BillingError(
      'Failed to cancel subscription',
      'cancel_subscription_failed',
      response.status
    )
  }

  return response.json()
}

/**
 * Add an add-on (subscription item) to the user's existing subscription.
 * Requires active subscription. Add-on types: datapoints, ai_insights, team_seats.
 */
async function addSubscriptionItem(
  token: string,
  addonType: "datapoints" | "ai_insights" | "team_seats",
  quantity?: number
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(`${API_URL}/add-subscription-item`, {
    method: "POST",
    headers: buildAuthHeaders(token),
    credentials: "include",
    body: JSON.stringify({ addonType, quantity: quantity ?? 1 }),
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new BillingError(
      data.message || "Failed to add add-on",
      data.error || "add_subscription_item_failed",
      response.status
    )
  }
  return data
}

/**
 * Remove an add-on (subscription item) from the user's subscription.
 * Requires active subscription. Add-on types: datapoints, ai_insights, team_seats.
 */
async function removeSubscriptionItem(
  token: string,
  addonType: "datapoints" | "ai_insights" | "team_seats",
  quantity?: number
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(`${API_URL}/remove-subscription-item`, {
    method: "POST",
    headers: buildAuthHeaders(token),
    credentials: "include",
    body: JSON.stringify({ addonType, quantity: quantity ?? 1 }),
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new BillingError(
      data.message || "Failed to remove add-on",
      data.error || "remove_subscription_item_failed",
      response.status
    )
  }
  return data
}

/**
 * Resume a canceled subscription
 */
async function resumeSubscription(token: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_URL}/resume`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
    credentials: 'include',
  })

  if (!response.ok) {
    throw new BillingError(
      'Failed to resume subscription',
      'resume_subscription_failed',
      response.status
    )
  }

  return response.json()
}
